/**
 * BeneficialOwnersList — view-only list of Beneficial Controlling Owners
 * for a Persona Moral customer, with an on-demand "Descargar CURP" button
 * per row.
 *
 * Why a separate component:
 *  - CustomerDetail is already ~1.5K lines; keeping this isolated.
 *  - Mirrors the LegalRepresentatives "download CURP per row" pattern.
 *  - PR2 acceptance: each subject of a moral expediente must be able to
 *    request their own CURP PDF independently (no bulk fan-out).
 */
import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Users, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fullName(bo) {
  return (
    (bo.full_name || "").trim() ||
    [bo.first_name, bo.paternal_surname, bo.maternal_surname].filter(Boolean).join(" ")
  );
}

export default function BeneficialOwnersList({ owners = [], onRefresh }) {
  const [downloadingId, setDownloadingId] = useState(null);

  if (!owners || owners.length === 0) {
    return (
      <div
        data-testid="bos-empty"
        className="bg-slate-900 border border-slate-800 rounded-xl p-5 text-sm text-slate-500"
      >
        Sin beneficiarios controladores registrados.
      </div>
    );
  }

  async function downloadCurp(bo) {
    if (!bo.owner_id) return;
    setDownloadingId(bo.owner_id);
    try {
      const r = await axios.post(
        `${API}/curp/download-pdf/beneficial-owner/${bo.owner_id}`,
        {},
        { withCredentials: true },
      );
      if (r.data?.success) {
        toast.success(r.data.message || "Constancia CURP descargada");
      } else {
        toast.error(r.data?.message || "No se pudo descargar la CURP");
      }
      if (typeof onRefresh === "function") await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error descargando CURP");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-5"
      data-testid="beneficial-owners-list"
    >
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-4 h-4 text-teal-400" />
        <h3 className="text-sm font-semibold text-white">
          Beneficiarios Controladores ({owners.length})
        </h3>
      </div>

      <div className="space-y-3">
        {owners.map((bo) => (
          <div
            key={bo.owner_id}
            data-testid={`bo-row-${bo.owner_id}`}
            className="rounded-lg border border-slate-800 bg-slate-900/40 p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-100 truncate">
                  {fullName(bo) || "Sin nombre"}
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-400 mt-1">
                  {bo.curp && (
                    <div>
                      <dt className="text-slate-500">CURP</dt>
                      <dd className="text-slate-200 font-mono">{bo.curp}</dd>
                    </div>
                  )}
                  {bo.rfc && (
                    <div>
                      <dt className="text-slate-500">RFC</dt>
                      <dd className="text-slate-200 font-mono">{bo.rfc}</dd>
                    </div>
                  )}
                  {bo.control_type && (
                    <div>
                      <dt className="text-slate-500">Tipo Control</dt>
                      <dd className="text-slate-200">{bo.control_type}</dd>
                    </div>
                  )}
                  {(bo.direct_ownership_pct || bo.indirect_ownership_pct) ? (
                    <div>
                      <dt className="text-slate-500">% Participación</dt>
                      <dd className="text-slate-200">
                        D: {bo.direct_ownership_pct ?? "—"}% · I:{" "}
                        {bo.indirect_ownership_pct ?? "—"}%
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </div>

              {bo.curp && (
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`download-curp-bo-${bo.owner_id}`}
                    disabled={downloadingId === bo.owner_id}
                    onClick={() => downloadCurp(bo)}
                  >
                    <Download className="w-3.5 h-3.5 mr-1" />
                    {downloadingId === bo.owner_id ? "Descargando…" : "Descargar CURP"}
                  </Button>
                  {bo.curp_pdf_status === "downloaded" && (
                    <span className="text-xs text-emerald-400">PDF disponible</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
